'use client';

import { useRef, useCallback, useEffect, useState } from 'react';
import { Masonry as MasonicGrid, useInfiniteLoader } from 'masonic';
import type { CommunityPost } from '@/lib/community/types';
import { Loader2 } from 'lucide-react';

/**
 * PostFeed — masonry layout feed with infinite scroll.
 * Uses masonic for height-aware shortest-column placement.
 *
 * Key design decisions:
 * - Masonic's positioner caches item indices in a persistent ref.
 *   If the items array shrinks below cached size, range() returns
 *   out-of-bounds indices → items[index] is undefined → WeakMap.set(undefined)
 *   crashes. We prevent this by tracking the longest items array and never
 *   passing a shorter one. A key reset handles feed changes (tab/filter switch).
 * - useInfiniteLoader is configured with totalItems = current count, so it
 *   won't trigger loads by itself. An IntersectionObserver sentinel handles
 *   the actual infinite loading.
 */
export function PostFeed({
	posts,
	isLoading,
	hasMore,
	onLoadMore,
	renderCard,
	headerSlot,
	emptyMessage = '暂无内容',
	columns = 2,
}: {
	posts: CommunityPost[];
	isLoading: boolean;
	hasMore: boolean;
	onLoadMore: () => void;
	renderCard: (post: CommunityPost) => React.ReactNode;
	headerSlot?: React.ReactNode;
	emptyMessage?: string;
	/** Max columns on desktop (2-5). Mobile always 1. */
	columns?: number;
}) {
	const maxCols = Math.min(Math.max(columns, 2), 5);
	const containerRef = useRef<HTMLDivElement>(null);
	const renderCardRef = useRef(renderCard);
	renderCardRef.current = renderCard;
	const loadingRef = useRef(isLoading);
	loadingRef.current = isLoading;
	const hasMoreRef = useRef(hasMore);
	hasMoreRef.current = hasMore;

	const columnGutter = 16;
	const rowGutter = 16;

	// Filter out invalid entries and duplicate ids before masonic receives them.
	const validPosts = posts.filter((post, index, arr) => (
		Boolean(post) && arr.findIndex((item) => item?.id === post.id) === index
	)) as CommunityPost[];

	// ── Masonic WeakMap crash prevention ──
	// Masonic's internal positioner caches indices. If we pass fewer items
	// than it cached, items[index] is undefined and WeakMap.set(undefined) throws.
	// We track the longest array and only ever pass ≥ that many items.
	const stableItemsRef = useRef<CommunityPost[]>([]);
	const [masonryKey, setMasonryKey] = useState(0);

	// Detect loading → loaded transition (happens on tab/filter change).
	// When loading finishes with new (shorter) data, reset masonry completely.
	useEffect(() => {
		if (!isLoading && validPosts.length > 0 && validPosts.length < stableItemsRef.current.length) {
			stableItemsRef.current = validPosts;
			setMasonryKey((k) => k + 1);
		}
	}, [isLoading, validPosts.length]);

	// Normal growth path: update ref when items grow
	if (validPosts.length >= stableItemsRef.current.length) {
		stableItemsRef.current = validPosts;
	}
	// When validPosts temporarily shrinks, stableItemsRef keeps the old (longer)
	// array — masonic never sees fewer items than its positioner cached.

	const masonryItems = stableItemsRef.current;

	const loadMoreItems = useCallback(
		async (_startIndex: number, _stopIndex: number, _items: CommunityPost[]) => {
			if (hasMoreRef.current && !loadingRef.current) {
				onLoadMore();
			}
		},
		[onLoadMore],
	);

	const maybeLoadMore = useInfiniteLoader<CommunityPost, (s: number, e: number, items: CommunityPost[]) => Promise<void>>(loadMoreItems, {
		isItemLoaded: (index, items) => index < items.length,
		totalItems: masonryItems.length,
	});

	const itemKey = useCallback((data: CommunityPost | undefined, _index: number) => {
		return data?.id ?? String(_index);
	}, []);

	const MasonryCard = useCallback(({ data }: { index: number; data: CommunityPost; width: number }) => {
		if (!data) return null;
		return <div style={{ marginBottom: rowGutter, contain: 'layout' }}>{renderCardRef.current(data)}</div>;
	}, []);

	// Cleanup sentinel observer on unmount
	const sentinelRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!hasMore) return;
		const el = sentinelRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && !loadingRef.current) {
					onLoadMore();
				}
			},
			{ rootMargin: '200px' },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasMore, onLoadMore]);

	if (!isLoading && validPosts.length === 0) {
		return (
			<div className="w-full">
				{headerSlot}
				<div className="flex flex-col items-center justify-center py-20 text-center">
					<p className="text-muted-foreground text-sm">{emptyMessage}</p>
					<p className="text-muted-foreground/60 text-xs mt-1">先逛逛，看看别人都在用什么</p>
				</div>
			</div>
		);
	}

	if (isLoading && validPosts.length === 0) {
		return (
			<div className="w-full">
				{headerSlot}
				<div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${maxCols}, minmax(0, 1fr))` }}>
					{Array.from({ length: 6 }).map((_, i) => (
						<div className="liquid-glass-card rounded-md overflow-hidden" key={`skeleton-${i}`}>
							<div className="aspect-video w-full animate-pulse bg-foreground/10" />
							<div className="p-3 flex flex-col gap-2">
								<div className="h-4 w-3/4 rounded bg-foreground/10 animate-pulse" />
								<div className="h-3 w-full rounded bg-foreground/10 animate-pulse" />
								<div className="flex items-center justify-between pt-1">
									<div className="flex items-center gap-1.5">
										<div className="w-5 h-5 rounded-full bg-foreground/10 animate-pulse" />
										<div className="h-3 w-12 rounded bg-foreground/10 animate-pulse" />
									</div>
									<div className="flex gap-2">
										<div className="h-3 w-6 rounded bg-foreground/10 animate-pulse" />
										<div className="h-3 w-6 rounded bg-foreground/10 animate-pulse" />
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="w-full" ref={containerRef}>
			{headerSlot}

			<MasonicGrid
				columnGutter={columnGutter}
				columnWidth={220}
				itemKey={itemKey}
				items={masonryItems}
				key={masonryKey}
				maxColumnCount={maxCols}
				onRender={maybeLoadMore}
				overscanBy={3}
				render={MasonryCard}
			/>

			{/* Extra sentinel for infinite scroll reliability */}
			{hasMore && <div className="h-1" ref={sentinelRef} />}

			{isLoading && (
				<div className="flex items-center justify-center py-8 gap-2">
					<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
					<span className="text-sm text-muted-foreground">加载中…</span>
				</div>
			)}

			{!hasMore && validPosts.length > 0 && (
				<div className="text-center py-8">
					<p className="text-xs text-muted-foreground">见底了，该你上场了</p>
				</div>
			)}
		</div>
	);
}
